import React, { createContext, useContext, useMemo } from 'react';
import { Auth0Provider, AppState, useAuth0 } from '@auth0/auth0-react';
import { apiConfig } from '../../config/api';
import { extractSubdomainSlug } from '../../utils/extractSubdomainSlug';
import { writeSelectedStaffId } from '../../common/staff/staffIdentityStorage';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: any;
    email: string | undefined;
    getAccessTokenSilently: (options?: { cacheMode?: 'on' | 'off' | 'cache-only' }) => Promise<string>;
    loginWithRedirect: () => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an Auth0ProviderWithConfig');
    }
    return context;
};

interface Auth0ProviderWithConfigProps {
    children: React.ReactNode;
}

const Auth0ProviderWithConfig: React.FC<Auth0ProviderWithConfigProps> = ({ children }) => {
    const { domain, clientId, audience } = apiConfig.auth0;

    // Forward the per-tenant slug (derived from the hostname) to Auth0 on every
    // /authorize call — login AND signup — as event.request.query.client_slug.
    // The backend resolves the tenant from the URL/slug when it provisions the
    // member (POST /members/create, POST /auth/sync); the slug is what carries
    // that tenant through signup. Without it a new-stack signup reaches Auth0 with
    // no slug and the user ends up with no client (historically it fell through to
    // a legacy default club, FORT-219 — that default has since been removed). On
    // the apex / www / localhost the slug is null and the param is simply omitted.
    //
    // Computed before the config guard below so the hook order stays stable
    // (react-hooks/rules-of-hooks).
    const authorizationParams = useMemo<Record<string, string>>(() => {
        const params: Record<string, string> = {
            redirect_uri: window.location.origin,
            audience: audience,
            scope: "openid profile email offline_access",
        };
        const tenantSlug = extractSubdomainSlug(window.location.hostname);
        if (tenantSlug) {
            params.client_slug = tenantSlug;
        }
        return params;
    }, [audience]);

    if (!domain || !clientId) {
        console.error('Auth0 configuration is missing');
        return <div>Error: Auth0 configuration is missing</div>;
    }

    const onRedirectCallback = (appState?: AppState) => {
        window.location.href = '/home';
    };

    return (
        <Auth0Provider
            domain={domain}
            clientId={clientId}
            authorizationParams={authorizationParams}
            onRedirectCallback={onRedirectCallback}
            useRefreshTokens={true}
            useRefreshTokensFallback={false}
            cacheLocation="localstorage"
        >
            <AuthContextProvider>{children}</AuthContextProvider>
        </Auth0Provider>
    );
};

const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const {
        isAuthenticated,
        isLoading,
        user,
        getAccessTokenSilently,
        loginWithRedirect,
        logout
    } = useAuth0();

    const value = {
        isAuthenticated,
        isLoading,
        user,
        email: user?.email,
        getAccessTokenSilently,
        loginWithRedirect,
        // Clear the device's staff selection too — it outlives the Auth0 session by design,
        // so without this the next person to log in here inherits the last one's identity.
        // See hooks/useLogout, which is the equivalent for components using useAuth0.
        logout: () => {
            writeSelectedStaffId(null);
            logout({ logoutParams: { returnTo: window.location.origin } });
        }
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default Auth0ProviderWithConfig;